import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import React from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import fuzzydate from 'util/fuzzydate';

import {AnalysisSelectorQuery} from './__generated/AnalysisSelectorQuery';

type AnalysisSelectorSummary = AnalysisSelectorQuery['analysisListing']['analyses'][number];

export const ANALYSIS_SELECTOR_QUERY = gql`
  query AnalysisSelectorQuery @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    analysisListing {
      analyses {
        id
        name
        permissions {
          writable
        }
        archiveRecord {
          purpose
        }
        modifiedAt
      }
    }
  }
`;

export interface AnalysisSelectorProps {
  selectedAnalysisId: string | null;
  onSelect: (analysis: AnalysisSelectorSummary) => void;
  disabled?: boolean;
  /** If true, read-only analyses will be disabled with a "You do not have permission to edit this analysis"
   * tooltip. Otherwise, all analyses will be shown and selectable. */
  disableReadOnlyAnalyses?: boolean;
  hideArchivedAnalyses?: boolean;
}

export default function AnalysisSelector(props: AnalysisSelectorProps): JSX.Element | null {
  const analysisListingResult = useBenchlingQuery<AnalysisSelectorQuery>(ANALYSIS_SELECTOR_QUERY, {
    fetchPolicy: 'network-only',
  });

  if (analysisListingResult.type === QueryResultType.ERROR) {
    return null;
  } else if (analysisListingResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  function sortAndFilterAnalyses(analyses: Array<AnalysisSelectorSummary>): Array<AnalysisSelectorSummary> {
    const filteredAnalyses = props.hideArchivedAnalyses
      ? analyses.filter((analysis) => !isArchived(analysis))
      : analyses;

    if (props.disableReadOnlyAnalyses) {
      const readOnlyAnalyses = naturalSortByKey(
        filteredAnalyses.filter((analysis) => !analysis.permissions.writable),
        'name'
      );
      const writableAnalyses = naturalSortByKey(
        filteredAnalyses.filter((analysis) => analysis.permissions.writable),
        'name'
      );
      return writableAnalyses.concat(readOnlyAnalyses); // sort read-only analyses to the end
    } else {
      return naturalSortByKey(filteredAnalyses, 'name');
    }
  }

  const analyses = sortAndFilterAnalyses(analysisListingResult.data.analysisListing.analyses);

  const menuOptions: Array<OptionData> = analyses.map((analysis) => ({
    name: analysis.name,
    value: analysis.id,
    metadata: `Last modified ${fuzzydate(analysis.modifiedAt)}`,
    disabled: props.disableReadOnlyAnalyses && !analysis.permissions.writable,
    disabledMessage: 'You do not have permission to edit this analysis',
  }));

  if (props.disableReadOnlyAnalyses) {
    const readOnlyHeader: OptionData = {
      isHeader: true,
      name: 'Read-only Analyses',
    };
    const lastWritableIndex = menuOptions.findLastIndex((menuOption) => !menuOption.disabled);
    menuOptions.splice(lastWritableIndex + 1, 0, readOnlyHeader);
  }

  const handleSelect = (analysisId: string): void => {
    const selectedAnalysis = analyses.find((analysis) => analysis.id === analysisId);
    assert(selectedAnalysis != null, 'Analysis not found', {analysisId});
    props.onSelect(selectedAnalysis);
  };

  function getDisplayName(): string {
    if (props.selectedAnalysisId == null) {
      return 'Select analysis...';
    }
    const selectedAnalysis = analyses.find((analysis) => analysis.id === props.selectedAnalysisId);
    return selectedAnalysis?.name ?? 'Analysis not found';
  }

  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          onSelect={handleSelect}
          optionDatas={menuOptions}
          selectedValue={props.selectedAnalysisId}
          width={Menu.WIDTHS.FULL}
        />
      }
      displayName={getDisplayName()}
      disabled={props.disabled}
      dataTestAttrs={{
        component: 'AnalysisSelector',
        element: 'root',
      }}
    />
  );
}
